import { AlertTriangle, Info } from "lucide-react";
import {
  getStatusColorClass,
  extractProviderErrors,
  looksEmpty,
  matchSpecForField
} from "./utils";

/* ══════════════════════════════════════════════════════════════════════════
   Response
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * Provider validation errors, listed and linked to the field that caused them.
 * Providers return these as a structured array; buried in a raw JSON dump
 * they're easy to miss and hard to act on.
 */
const ProviderErrors = ({ errors, specs, onFocusField }) => (
  <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-4">
    <div className="flex items-center gap-2 mb-2">
      <AlertTriangle className="w-4 h-4 text-red-600" />
      <h5 className="text-sm font-semibold text-red-900">
        The provider rejected this request
      </h5>
    </div>
    <ul className="space-y-1.5">
      {errors.map((e, i) => {
        const spec = matchSpecForField(specs, e.fieldHint);
        return (
          <li key={i} className="flex items-start gap-2 text-sm text-red-800">
            {e.code != null && (
              <span className="font-mono text-[10px] bg-white border border-red-200 text-red-600 rounded px-1.5 py-0.5 shrink-0 mt-0.5">
                {e.code}
              </span>
            )}
            <span className="flex-1">{e.message}</span>
            {spec && onFocusField && (
              <button
                onClick={() => onFocusField(spec.key)}
                className="text-xs text-blue-600 underline shrink-0"
              >
                Go to {spec.label}
              </button>
            )}
          </li>
        );
      })}
    </ul>
  </div>
);

/**
 * A successful call whose upstream simply had no records. Previously
 * indistinguishable from a failure — a near-empty response reads as a bug.
 */
const NoDataNotice = () => (
  <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
    <div className="flex items-center gap-2 mb-1">
      <Info className="w-4 h-4 text-amber-600" />
      <h5 className="text-sm font-semibold text-amber-900">
        Succeeded — but the provider returned no data
      </h5>
    </div>
    <p className="text-sm text-amber-800">
      The request was accepted. The upstream has no records matching these
      inputs, so the requested segments were omitted rather than returned empty.
      This is not an error — try a different identifier.
    </p>
  </div>
);

export const ApiResponseDisplay = ({ response, specs, onFocusField }) => {
  if (!response) return null;

  const errors = extractProviderErrors(response.data);
  const empty = !errors.length && looksEmpty(response.data);
  const segments =
    response.data && typeof response.data === "object"
      ? Object.keys(
          (response.data.json && typeof response.data.json === "object"
            ? response.data.json.results
            : response.data.results) || {}
        )
      : [];

  return (
    <div>
      <div className="mb-3 flex items-center gap-4 text-sm flex-wrap">
        <span
          className={`font-semibold ${getStatusColorClass(response.status)}`}
        >
          Status: {response.status} {response.statusText}
        </span>
        {response.time !== "N/A" && (
          <span className="text-gray-600">Time: {response.time}</span>
        )}
        {response.size !== "N/A" && (
          <span className="text-gray-600">Size: {response.size}</span>
        )}
      </div>

      {errors.length > 0 && (
        <ProviderErrors
          errors={errors}
          specs={specs}
          onFocusField={onFocusField}
        />
      )}
      {empty && <NoDataNotice />}

      {segments.length > 1 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {segments.map((s) => (
            <span
              key={s}
              className="text-[11px] font-mono bg-gray-100 border border-gray-200 text-gray-600 rounded-full px-2 py-0.5"
            >
              {s}
            </span>
          ))}
        </div>
      )}

      <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
        <pre className="text-green-400 text-sm font-mono whitespace-pre-wrap break-words">
          {typeof response.data === "string"
            ? response.data
            : JSON.stringify(response.data, null, 2)}
        </pre>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════════════
   Parameters — one control per schema type
   ══════════════════════════════════════════════════════════════════════════ */

const FieldLabel = ({ spec }) => (
  <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
    <span>{spec.label}</span>
    {spec.required && <span className="text-red-500 font-bold">*</span>}
    <span className="ml-auto font-mono text-[10px] font-normal text-gray-400">
      {spec.key}
    </span>
  </label>
);

const Hint = ({ problem, spec }) => {
  const text =
    problem?.message ||
    spec.describe ||
    (spec.maxLength ? `Max ${spec.maxLength} characters` : "");
  if (!text) return null;
  return (
    <p className={`text-xs mt-1 ${problem ? "text-red-600" : "text-gray-400"}`}>
      {text}
    </p>
  );
};

/**
 * Booleans select which segments the provider returns — the whole point of many
 * endpoints. As text boxes they looked like arbitrary strings, and editing one
 * turned it into the string "true", which providers reject.
 */
const BooleanField = ({ spec, value, onChange }) => {
  const on = value === true || value === "true";
  return (
    <div>
      <FieldLabel spec={spec} />
      <button
        type="button"
        onClick={() => onChange(spec.key, !on)}
        className="w-full flex items-center gap-3 px-3 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition-colors"
      >
        <span
          className={`relative w-9 h-5 rounded-full transition-colors shrink-0 ${
            on ? "bg-blue-600" : "bg-gray-300"
          }`}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
              on ? "translate-x-4" : ""
            }`}
          />
        </span>
        <span className="text-sm text-gray-700">
          {on ? "Included" : "Excluded"}
        </span>
        <span className="ml-auto font-mono text-xs text-gray-400">
          {String(on)}
        </span>
      </button>
      <Hint spec={spec} />
    </div>
  );
};

/**
 * Multi-select for array parameters. A batched-segment request can carry ~30
 * members; as one long JSON string in a text field it was effectively unusable,
 * and any edit broke the array.
 */
const ArrayField = ({ spec, value, onChange }) => {
  const selected = Array.isArray(value) ? value : [];
  const options = spec.arrayOptions;

  if (!options || !options.length) {
    // No documented member list — fall back to a comma-separated editor rather
    // than making the user hand-write JSON.
    return (
      <div className="col-span-2">
        <FieldLabel spec={spec} />
        <input
          type="text"
          value={selected.join(", ")}
          onChange={(e) =>
            onChange(
              spec.key,
              e.target.value
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
            )
          }
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
        />
        <p className="text-xs mt-1 text-gray-400">
          Comma-separated. Sent as a JSON array.
        </p>
      </div>
    );
  }

  const toggle = (o) =>
    onChange(
      spec.key,
      selected.includes(o) ? selected.filter((x) => x !== o) : [...selected, o]
    );

  return (
    <div className="col-span-2">
      <FieldLabel spec={spec} />
      <div className="flex items-center gap-3 text-xs text-gray-400 mb-1.5">
        <span>
          {selected.length} of {options.length} selected
        </span>
        <button
          type="button"
          onClick={() => onChange(spec.key, [...options])}
          className="text-blue-600 underline"
        >
          Select all
        </button>
        <button
          type="button"
          onClick={() => onChange(spec.key, [])}
          className="text-blue-600 underline"
        >
          Clear
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5 p-2 border border-gray-300 rounded-lg bg-gray-50 max-h-40 overflow-y-auto">
        {options.map((o) => {
          const on = selected.includes(o);
          return (
            <button
              key={o}
              type="button"
              onClick={() => toggle(o)}
              className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                on
                  ? "bg-blue-600 border-blue-600 text-white font-medium"
                  : "bg-white border-gray-300 text-gray-600 hover:border-gray-400"
              }`}
            >
              {o}
            </button>
          );
        })}
      </div>
      <p className="text-xs mt-1 text-gray-400">
        One call returns every selected item. Sent as a JSON array.
      </p>
    </div>
  );
};

const EnumField = ({ spec, value, onChange }) => (
  <div>
    <FieldLabel spec={spec} />
    <select
      value={value ?? ""}
      onChange={(e) => onChange(spec.key, e.target.value)}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
    >
      <option value="">— not set —</option>
      {spec.options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
    <Hint spec={spec} />
  </div>
);

const TextField = ({ spec, value, onChange, problem, inputRef }) => (
  <div>
    <FieldLabel spec={spec} />
    <input
      ref={inputRef}
      type={spec.control === "number" ? "number" : "text"}
      value={value ?? ""}
      onChange={(e) => onChange(spec.key, e.target.value)}
      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
        problem
          ? "border-red-400 bg-red-50 focus:ring-red-100"
          : "border-gray-300 focus:ring-blue-100 focus:border-blue-500"
      }`}
    />
    <Hint spec={spec} problem={problem} />
  </div>
);

export const ParametersSection = ({
  specs,
  parameters,
  problems = [],
  loadingFixtures,
  onParameterChange,
  fieldRefs
}) => {
  const problemFor = (key) => problems.find((p) => p.key === key);

  return (
    <div className="mb-6">
      <h4 className="text-base font-semibold text-gray-900 mb-3">Parameters</h4>
      {loadingFixtures ? (
        <div className="flex items-center justify-center py-8 bg-gray-50 rounded-lg">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-2"></div>
          <span className="text-sm text-gray-600">Loading parameters...</span>
        </div>
      ) : specs.length > 0 ? (
        <div className="grid grid-cols-2 gap-x-4 gap-y-3">
          {specs.map((spec) => {
            const value = parameters[spec.key];
            const common = {
              spec,
              value,
              onChange: onParameterChange,
              problem: problemFor(spec.key)
            };
            if (spec.control === "boolean")
              return <BooleanField key={spec.key} {...common} />;
            if (spec.control === "array")
              return <ArrayField key={spec.key} {...common} />;
            if (spec.control === "enum")
              return <EnumField key={spec.key} {...common} />;
            return (
              <TextField
                key={spec.key}
                {...common}
                inputRef={
                  fieldRefs ? (el) => (fieldRefs.current[spec.key] = el) : null
                }
              />
            );
          })}
        </div>
      ) : (
        <p className="text-sm text-gray-500 bg-gray-50 rounded-lg p-4">
          No parameters required or available for this endpoint.
        </p>
      )}
    </div>
  );
};
